import { SttAdapter, SttCapabilities, SttResult } from './stt_adapter';

export class MoonshineAdapter implements SttAdapter {
  readonly capabilities: SttCapabilities = {
    isLocal: true,
    runtime: 'transformers.js-wasm',
    modelId: 'moonshine-tiny-ar',
  };

  private worker: Worker | null = null;
  private messageIdCounter = 0;
  private pendingResolvers = new Map<number, (res: SttResult) => void>();
  private isDisposed = false;

  constructor() {
    // Lazy worker creation in init
  }

  async init(onProgress?: (phase: string, progress: number) => void): Promise<void> {
    if (this.worker) return;

    return new Promise<void>((resolve, reject) => {
      try {
        this.worker = new Worker(new URL('./stt_worker.ts', import.meta.url), {
          type: 'module',
        });

        this.worker.onmessage = (e: MessageEvent) => {
          const { type, id, text, latencyMs, phase, progress, error } = e.data;

          if (type === 'init_done') {
            resolve();
          } else if (type === 'init_progress') {
            onProgress?.(phase, progress);
          } else if (type === 'init_error') {
            reject(new Error(error));
          } else if (type === 'transcribe_result') {
            const resolver = this.pendingResolvers.get(id);
            if (resolver) {
              this.pendingResolvers.delete(id);
              resolver({ text, latencyMs });
            }
          } else if (type === 'transcribe_error') {
            const resolver = this.pendingResolvers.get(id);
            if (resolver) {
              this.pendingResolvers.delete(id);
              resolver({ text: '', latencyMs: 0 });
            }
          }
        };

        this.worker.onerror = (err) => {
          reject(new Error(`Worker error: ${err.message}`));
        };

        this.worker.postMessage({ type: 'init' });
      } catch (err) {
        reject(err);
      }
    });
  }

  async transcribe(audio: Float32Array): Promise<SttResult> {
    if (this.isDisposed || !this.worker) {
      return { text: '', latencyMs: 0 };
    }

    const id = ++this.messageIdCounter;

    return new Promise<SttResult>((resolve) => {
      this.pendingResolvers.set(id, resolve);
      // Transfer audio buffer to worker
      this.worker?.postMessage(
        {
          type: 'transcribe',
          id,
          audio,
        },
        [audio.buffer]
      );
    });
  }

  cancel(): void {
    // Clear pending resolvers
    for (const resolver of this.pendingResolvers.values()) {
      resolver({ text: '', latencyMs: 0 });
    }
    this.pendingResolvers.clear();
  }

  async dispose(): Promise<void> {
    this.isDisposed = true;
    this.cancel();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
